import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, MapPin, Check, Phone, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/AuthContext";
import PTAuthModal from "../components/pt/PTAuthModal";
import PTLanguageToggle from "../components/pt/PTLanguageToggle";

const HOW_STEPS = [
  { nl: ["Maak je account", "Maak je persoonlijke PT-account aan."], en: ["Create your account", "Create your personal PT account."] },
  { nl: ["Vertel je doelen", "Geef aan wat je doelen zijn en welk pakket je voorkeur heeft."], en: ["Tell us your goals", "Share your goals and which package you prefer."] },
  { nl: ["Boek je sessie", "Bekijk beschikbare tijden en plan je training."], en: ["Book your session", "Check available times and schedule your training."] },
  { nl: ["Start training", "Train 1-op-1 aan jouw persoonlijke doelen."], en: ["Start training", "Train 1-on-1 towards your personal goals."] },
  { nl: ["Volg je voortgang", "Bekijk trainingen, feedback en ontwikkeling in je account."], en: ["Track your progress", "See sessions, feedback and progress in your account."] },
];
const PT_INCLUDES = [
  ["Boxing fundamentals", "Boxing fundamentals"], ["Houding & stance", "Stance"], ["Footwork", "Footwork"],
  ["Punching technique", "Punching technique"], ["Combinaties", "Combinations"], ["Defence", "Defence"],
  ["Pads", "Pads"], ["Conditie", "Conditioning"], ["Coördinatie", "Coordination"], ["Technische ontwikkeling", "Technical development"],
];
const FOR_WHO = [
  { nl: "Complete beginners", en: "Complete beginners" },
  { nl: "Recreatieve boksers", en: "Recreational boxers" },
  { nl: "Conditie verbeteren", en: "Improve conditioning" },
  { nl: "Technisch beter leren boksen", en: "Level up your technique" },
];

export default function PT() {
  const { lang, t } = useT();
  const nav = useNavigate();
  const { user, ready, signIn, register, applyGoogleSession } = useAuth();
  const [settings, setSettings] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("register");
  const [pref, setPref] = useState("");
  const [allPkgs, setAllPkgs] = useState(false);
  const [buy, setBuy] = useState(null);
  const [buyForm, setBuyForm] = useState({ first_name: "", last_name: "", email: "" });
  const [busy, setBusy] = useState(false);

  // Google OAuth return op /pt verwerken (#session_id)
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) return;
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        await applyGoogleSession(data.token, data.user);
        window.history.replaceState(null, "", "/pt");
        toast.success(t("pt.auth.googleSuccess"));
        nav("/pt/dashboard");
      } catch {
        toast.error(t("pt.auth.googleError"));
      }
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    api.get("/pt/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setBuyForm({
      first_name: user.first_name || user.name?.split(" ")[0] || "",
      last_name: user.last_name || user.name?.split(" ").slice(1).join(" ") || "",
      email: user.email || "",
    });
  }, [user]);

  const L = (nl_, en) => (lang === "en" ? en : nl_);
  const price = (v) => `€${Number(v || 0).toFixed(0)}`;
  const packages = settings?.packages || [];
  const single = packages.find((p) => p.key === "single");
  const featured = packages.filter((p) => p.featured && p.key !== "single").slice(0, 2);

  const cta = () => {
    if (user) { nav("/pt/dashboard"); return; }
    setAuthMode("register");
    setAuthOpen(true);
  };
  const openLogin = () => { setAuthMode("login"); setAuthOpen(true); };

  const choosePreference = async (key) => {
    if (user) {
      try {
        await api.post("/pt/me/preference", { package_preference: key });
        toast.success(L("Voorkeur opgeslagen — we nemen contact met je op om je traject af te stemmen.", "Preference saved — we'll contact you to tailor your journey."));
        setAllPkgs(false);
        nav("/pt/dashboard");
      } catch { toast.error(L("Opslaan mislukt", "Saving failed")); }
      return;
    }
    setPref(key);
    setAllPkgs(false);
    setAuthMode("register");
    setAuthOpen(true);
  };

  const buyNow = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/pt/checkout", { kind: buy, ...buyForm, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pt.checkout.failed"));
      setBusy(false);
    }
  };

  const pkgCard = (p, dark) => (
    <div key={p.key} className={`border p-6 md:p-7 flex flex-col ${dark ? "bg-[#0A0A0A] text-[#F5F3F0] border-[#0A0A0A]" : "border-[#0A0A0A]"}`} data-testid={`pt-price-${p.key}`}>
      <div className={`chip ${dark ? "" : "text-[#4A4A4A]"}`} style={dark ? { color: "var(--joh-accent)" } : {}}>
        {L(p.name, p.name_en || p.name).toUpperCase()} · {p.sessions} {L(p.sessions > 1 ? "SESSIES" : "SESSIE", p.sessions > 1 ? "SESSIONS" : "SESSION")}
      </div>
      <div className="font-display text-5xl mt-3" data-testid={`pt-price-${p.key}-amount`}>{price(p.price)}</div>
      <div className={`text-sm mt-2 flex-1 ${dark ? "text-[#A3A3A3]" : "text-[#4A4A4A]"}`}>
        {L(p.description, p.description_en || p.description)}
        <span className="block mt-2 chip opacity-70">
          {p.sessions > 1 ? `≈ ${price(p.price / p.sessions)} ${L("per sessie", "per session")} · ` : ""}{L(`geldig ${p.validity_days} dagen`, `valid ${p.validity_days} days`)}
        </span>
      </div>
      <button onClick={() => choosePreference(p.key)} className="btn mt-6 justify-center" data-testid={`pt-pref-${p.key}`}>
        {L("KIES DIT PAKKET", "CHOOSE PACKAGE")}
      </button>
    </div>
  );

  return (
    <div className="bg-[#0A0A0A] text-[#F5F3F0]" data-testid="pt-page">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 md:px-12 py-6">
        <Link to="/" className="font-logo text-2xl tracking-tight" data-testid="pt-logo">JOH.</Link>
        <div className="flex items-center gap-5">
          <PTLanguageToggle compact />
          {user && <Link to="/pt/dashboard" className="chip text-[#DDD6CB] hover:text-white transition-colors" data-testid="pt-my-dashboard">{L("MIJN PT", "MY PT")}</Link>}
          <Link to="/" className="chip text-[#DDD6CB] hover:text-white transition-colors" data-testid="pt-back-home">← {L("HOME", "HOME")}</Link>
        </div>
      </header>

      {/* 1 — Hero met PT-video */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="pt-hero">
        {settings?.hero_video && (
          <video className="absolute inset-0 w-full h-full object-cover" src={settings.hero_video} autoPlay muted loop playsInline />
        )}
        <div className="absolute inset-0 bg-[#0A0A0A]/60" />
        <div className="absolute inset-0 grain grain-dark opacity-60 pointer-events-none" />
        <div className="relative z-10 px-6 md:px-12 pb-16 md:pb-24 w-full">
          <div className="chip mb-4" style={{ color: "var(--joh-accent)" }}>// {L("PERSONAL TRAINING — 1 OP 1", "PERSONAL TRAINING — 1 ON 1")}</div>
          <h1 className="font-display uppercase text-[11.5vw] md:text-[7.5vw] leading-[0.86]" data-testid="pt-hero-title">
            Boxing<br />Personal<br /><span className="font-serif-italic normal-case tracking-tight text-[#DDD6CB]">Training.</span>
          </h1>
          <p className="mt-6 max-w-xl text-[#DDD6CB] text-base md:text-lg">
            {L("Individuele boxing coaching bij JOH Boxing — volledig afgestemd op jouw niveau, ervaring, doelen en fysieke mogelijkheden.",
               "Individual boxing coaching at JOH Boxing — fully tailored to your level, experience, goals and physical abilities.")}
          </p>
          <div className="mt-8">
            <button onClick={cta} className="btn-pt-cta" data-testid="pt-cta-book">
              {L("BOEK EEN PERSONAL TRAINING", "BOOK A PERSONAL TRAINING")} <ArrowUpRight size={14}/>
            </button>
            {!user && (
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2" data-testid="pt-auth-links">
                <button onClick={openLogin} className="chip text-[#DDD6CB] underline underline-offset-4 hover:text-white" data-testid="pt-link-login">
                  {L("Heb je al een account? Log in", "Already have an account? Log in")}
                </button>
                <button onClick={() => { setAuthMode("register"); setAuthOpen(true); }} className="chip underline underline-offset-4 hover:text-white" style={{ color: "var(--joh-accent)" }} data-testid="pt-link-register">
                  {L("Nieuw bij JOH? Maak een account", "New to JOH? Create account")}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 1b — How it works */}
      <section className="py-24 md:py-32 px-6 md:px-12 grain grain-dark border-t border-[#F5F3F0]/10" data-testid="pt-how">
        <div className="max-w-6xl mx-auto">
          <div className="section-tag section-tag-dark">// {L("HOE HET WERKT", "HOW IT WORKS")}</div>
          <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] mt-4">{L("In vijf stappen", "In five steps")}<br /><span className="font-serif-italic normal-case">{L("aan de slag.", "you start.")}</span></h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {HOW_STEPS.map((s, i) => (
              <div key={i} className="border border-[#F5F3F0]/15 p-5 flex flex-col" data-testid={`pt-step-${i + 1}`}>
                <div className="chip" style={{ color: "var(--joh-accent)" }}>{String(i + 1).padStart(2, "0")}</div>
                <div className="font-display uppercase text-lg mt-3">{L(s.nl[0], s.en[0])}</div>
                <div className="text-sm text-[#A3A3A3] mt-2 flex-1">{L(s.nl[1], s.en[1])}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2 — Wat Personal Training inhoudt */}
      <section className="bg-[#F5F3F0] text-[#0A0A0A] py-24 md:py-32 px-6 md:px-12 grain" data-testid="pt-expect">
        <div className="max-w-6xl mx-auto">
          <div className="section-tag">// {L("WAT JE KUNT VERWACHTEN", "WHAT TO EXPECT")}</div>
          <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] mt-4">{L("Elke sessie", "Every session")}<br /><span className="font-serif-italic normal-case">{L("op maat.", "tailored.")}</span></h2>
          <p className="mt-6 max-w-2xl text-[#4A4A4A]">
            {L("Eén uur volledige focus op jou. De training wordt afgestemd op jouw niveau, ervaring, doelen, fysieke mogelijkheden en persoonlijke ontwikkeling — geen groep, geen afleiding.",
               "One hour fully focused on you. Training is tailored to your level, experience, goals, physical abilities and personal development — no group, no distractions.")}
          </p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="pt-expect-list">
            {PT_INCLUDES.map(([nl_, en], i) => (
              <div key={i} className="border border-[#0A0A0A]/20 px-4 py-3 chip flex items-center gap-2"><Check size={12}/>{L(nl_, en)}</div>
            ))}
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="pt-forwho">
            {FOR_WHO.map((w, i) => (
              <div key={i} className="border border-[#0A0A0A]/20 p-5">
                <div className="chip" style={{ color: "var(--joh-accent)" }}>{String(i + 1).padStart(2, "0")}</div>
                <div className="font-display uppercase text-xl mt-2">{L(w.nl, w.en)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 — Pricing: single + max 2 featured + view all */}
      <section id="pt-tarieven" className="bg-[#F5F3F0] text-[#0A0A0A] py-24 md:py-32 px-6 md:px-12 grain border-t border-[#0A0A0A]/10" data-testid="pt-pricing">
        <div className="max-w-6xl mx-auto">
          <div className="section-tag">// {L("TARIEVEN", "PRICING")}</div>
          <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] mt-4">{L("Investeer in", "Invest in")}<br /><span className="font-serif-italic normal-case">{L("jezelf.", "yourself.")}</span></h2>
          <p className="mt-6 max-w-2xl text-[#4A4A4A]" data-testid="pt-packages-note">
            {L("Selecteer je voorkeurspakket tijdens je registratie — wij nemen het vanaf daar over. Je betaalt pas nadat we samen je traject hebben afgestemd.",
               "Select your preferred package during registration. We'll take it from there — you only pay after we've tailored your journey together.")}
          </p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {single && (
              <div className="border border-[#0A0A0A] p-6 md:p-7 flex flex-col" data-testid="pt-price-single">
                <div className="chip text-[#4A4A4A]">{L("SINGLE SESSION", "SINGLE SESSION")}</div>
                <div className="font-display text-5xl mt-3" data-testid="pt-price-single-amount">{price(single.price)}</div>
                <div className="text-sm mt-2 flex-1 text-[#4A4A4A]">{L("1 Personal Training sessie van 60 minuten.", "1 Personal Training session of 60 minutes.")}</div>
                <button onClick={() => choosePreference("single")} className="btn mt-6 justify-center" data-testid="pt-pref-single">{L("BOEK EEN SESSIE", "BOOK A SESSION")}</button>
              </div>
            )}
            {featured.map((p, i) => pkgCard(p, i === 0))}
          </div>
          {packages.filter((p) => p.key !== "single" && !p.featured).length > 0 && (
            <button onClick={() => setAllPkgs(true)} className="chip mt-8 underline underline-offset-4 text-[#4A4A4A] hover:text-[#0A0A0A]" data-testid="pt-view-all-packages">
              {L("BEKIJK ALLE PAKKETTEN", "VIEW ALL PACKAGES")} →
            </button>
          )}
        </div>
      </section>

      {/* 4 — Locatie & contact */}
      <section className="py-24 md:py-32 px-6 md:px-12 grain grain-dark" data-testid="pt-location">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="section-tag section-tag-dark">// {L("LOCATIE & CONTACT", "LOCATION & CONTACT")}</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] mt-4">{settings?.location_name || "JOH Boxing"}</h2>
            <p className="mt-6 text-[#A3A3A3] max-w-lg">{settings?.location_text}</p>
            <div className="mt-8 space-y-3" data-testid="pt-contact">
              <a href={`https://maps.google.com/?q=${encodeURIComponent(settings?.location_address || "Rhoneweg 38, 1043 AH Amsterdam")}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-3 chip text-[#DDD6CB] hover:text-white transition-colors" data-testid="pt-contact-address">
                <MapPin size={14} style={{ color: "var(--joh-accent)" }}/> {settings?.location_address || "Rhoneweg 38, 1043 AH Amsterdam"}
              </a>
              <a href={`tel:${(settings?.contact_phone || "").replace(/\s/g, "")}`} className="flex items-center gap-3 chip text-[#DDD6CB] hover:text-white transition-colors" data-testid="pt-contact-phone">
                <Phone size={14} style={{ color: "var(--joh-accent)" }}/> {settings?.contact_phone || "+31 6 26923741"}
              </a>
              <a href={`mailto:${settings?.contact_email || "team@johboxing.com"}`} className="flex items-center gap-3 chip text-[#DDD6CB] hover:text-white transition-colors" data-testid="pt-contact-email">
                <Mail size={14} style={{ color: "var(--joh-accent)" }}/> {settings?.contact_email || "team@johboxing.com"}
              </a>
            </div>
          </div>
          <div className="space-y-3">
            {[
              [L("Bereikbaarheid", "Accessibility"), L("Goed bereikbaar met OV en auto; parkeergelegenheid in de buurt.", "Easy to reach by public transport and car; parking nearby.")],
              [L("Faciliteiten", "Facilities"), L("Ring, pads, bokszakken en krachtruimte — alles onder één dak.", "Ring, pads, heavy bags and strength area — all under one roof.")],
              [L("1-op-1 ruimte", "Private space"), L("Persoonlijke trainingszone zonder groepsdruk.", "Personal training zone without group pressure.")],
            ].map(([t2, d], i) => (
              <div key={i} className="border border-[#F5F3F0]/15 p-5 flex gap-4 items-start">
                <MapPin size={16} className="mt-1 shrink-0" style={{ color: "var(--joh-accent)" }}/>
                <div><div className="chip text-[#DDD6CB]">{t2}</div><div className="text-sm text-[#A3A3A3] mt-1">{d}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#F5F3F0]/10 px-6 md:px-12 py-8 flex flex-wrap items-center justify-between gap-3">
        <span className="font-logo text-xl">JOH.</span>
        <span className="chip text-[#4A4A4A]">PERSONAL TRAINING — AMSTERDAM</span>
        <Link to="/" className="chip text-[#A3A3A3] hover:text-[#F5F3F0]" data-testid="pt-footer-home">JOHBOXING.COM</Link>
      </footer>

      {/* Alle pakketten-modal */}
      {allPkgs && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setAllPkgs(false)}>
          <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-2xl w-full p-6 md:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pt-all-packages">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="chip text-[#A3A3A3]">// {L("ALLE PT-PAKKETTEN", "ALL PT PACKAGES")}</div>
                <div className="font-display uppercase text-3xl mt-1">{L("Kies je traject.", "Choose your journey.")}</div>
              </div>
              <button onClick={() => setAllPkgs(false)} className="text-[#A3A3A3] hover:text-white" data-testid="pt-all-packages-close"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              {packages.map((p) => (
                <div key={p.key} className="border border-[#F5F3F0]/15 p-5 flex flex-wrap items-center gap-4" data-testid={`pt-allpkg-${p.key}`}>
                  <div className="flex-1 min-w-48">
                    <div className="font-display uppercase text-xl">{L(p.name, p.name_en || p.name)}</div>
                    <div className="chip text-[#A3A3A3] mt-1">{p.sessions} {L(p.sessions > 1 ? "sessies" : "sessie", p.sessions > 1 ? "sessions" : "session")} · {L(`geldig ${p.validity_days} dagen`, `valid ${p.validity_days} days`)}</div>
                    <div className="text-sm text-[#A3A3A3] mt-1">{L(p.description, p.description_en || p.description)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-3xl" style={{ color: "var(--joh-accent)" }}>{price(p.price)}</div>
                    <button onClick={() => choosePreference(p.key)} className="chip mt-2 px-4 py-2 border transition-colors" style={{ borderColor: "var(--joh-accent)", color: "var(--joh-accent)" }} data-testid={`pt-allpkg-choose-${p.key}`}>
                      {L("KIES DIT PAKKET", "CHOOSE PACKAGE")}
                    </button>
                    {user && (
                      <button onClick={() => { setAllPkgs(false); setBuy(p.key); }} className="chip block ml-auto mt-2 text-[#4A4A4A] underline underline-offset-4" data-testid={`pt-allpkg-buy-${p.key}`}>
                        {L("of direct kopen", "or buy directly")}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Koop-modal (alleen ingelogd, via alle-pakketten) */}
      {buy && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setBuy(null)}>
          <form onSubmit={buyNow} className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="pt-buy-modal">
            {(() => { const p = packages.find((x) => x.key === buy); return (
              <div className="chip text-[#A3A3A3]">// {L(p?.name || buy, p?.name_en || p?.name || buy).toUpperCase()} — {price(p?.price)}{p?.sessions > 1 ? ` · ${p.sessions} ${L("SESSIES", "SESSIONS")}` : ""}</div>
            ); })()}
            <div className="font-display uppercase text-3xl mt-1 mb-5">{L("Afrekenen.", "Checkout.")}</div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="chip text-[#A3A3A3]">{L("Voornaam", "First name")}*</label><input required value={buyForm.first_name} onChange={(e) => setBuyForm((p) => ({ ...p, first_name: e.target.value }))} className="editorial-input dark" data-testid="pt-buy-first" /></div>
                <div><label className="chip text-[#A3A3A3]">{L("Achternaam", "Last name")}*</label><input required value={buyForm.last_name} onChange={(e) => setBuyForm((p) => ({ ...p, last_name: e.target.value }))} className="editorial-input dark" data-testid="pt-buy-last" /></div>
              </div>
              <div><label className="chip text-[#A3A3A3]">{L("E-mailadres", "Email")}*</label><input required type="email" value={buyForm.email} onChange={(e) => setBuyForm((p) => ({ ...p, email: e.target.value }))} className="editorial-input dark" data-testid="pt-buy-email" /></div>
              <button type="submit" disabled={busy} className="w-full py-3.5 chip" style={{ background: "var(--joh-accent)", color: "#0A0A0A" }} data-testid="pt-buy-submit">{busy ? "…" : L("DOORGAAN NAAR BETALING", "CONTINUE TO PAYMENT")}</button>
            </div>
          </form>
        </div>
      )}

      {/* Auth-modal: volledige registratie / login / wachtwoord vergeten */}
      {authOpen && <PTAuthModal onClose={() => setAuthOpen(false)} onAuthed={() => { setAuthOpen(false); nav("/pt/dashboard"); }} signIn={signIn} register={register} initialMode={authMode} packages={packages} initialPref={pref} />}
    </div>
  );
}

